import type { AxiosError } from 'axios';
import { refreshSubscriptionQueries } from '@/lib/subscription-query-keys';
/**
 * My Account Page
 * 
 * User account management: profile, password, invoices, plan, 2FA.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User,
  Lock,
  Receipt,
  Sparkles,
  CreditCard,
  Settings,
  Mail,
  Phone,
  Building2,
  MapPin,
  Eye,
  EyeOff,
  Check,
  Download,
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Loader2,
  Shield,
  Crown,
  Clock,
  Info,
  Smartphone,
  KeyRound,
  Copy,
  FileText,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from '@/components/ui/input-otp';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mySubscriptionQueryOptions } from '@/hooks/use-subscription-status';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import {
  accountService,
  Invoice as AccountInvoice,
  type CancellationReason,
  type UserSubscription,
} from '@/services/account.service';
import planService, { Plan as BackendPlan } from '@/services/plan.service';
import userService from '@/services/user.service';
import { getSubscriberProfileCompletion } from '@/lib/subscriber-profile-completion';
import {
  CancelSubscriptionDialog,
  ImportantNotice,
  LockedActionHint,
  SubscriberProfileCompletionModal,
  TrialPlanStatus,
  TrialSubscribeDialog,
} from '@/components/subscription';
import { canSubscribeInTrial, isTrialConversionPending, trialExitToast } from '@/lib/trial-subscription';
import { CurrentPlanCycleAction, type BillingCycle } from '@/components/account/CurrentPlanCycleAction';
import { annualLockToMonthlyMessage, annualLockToPlanMessage } from '@/lib/plan-change-messages';
import {
  CANCEL_BUTTON_CLASS,
  FREE_PLAN_CANCEL_MESSAGE,
  cancellationScheduledText,
  freePlanScheduledText,
  scheduledNoticeText,
  trialFreePlanChangeNotice,
} from '@/lib/subscription-cancellation';
import { openInNewTab } from '@/lib/open-in-new-tab';
import { payPendingCharge } from '@/lib/pending-charge';
import AccountMembersCard from '@/components/account/AccountMembersCard';
import DeleteAccountCard from '@/components/account/DeleteAccountCard';
import { formatBrasilia } from '@/lib/brasiliaDate';
import { formatBRL } from '@/lib/currency';
import { MESES_NO_ANO, yearlyDiscountPercent } from '@/lib/plan-pricing';

// Types
/** Alvo de um pedido de plano: só o que o pedido usa (id para a API, nome
 * para os avisos). */
type PlanTarget = Pick<BackendPlan, 'id' | 'name'>;

interface Invoice {
  id: string;
  number: string;
  date: string;
  dueDate: string;
  amount: number;
  status: 'paid' | 'pending' | 'overdue' | 'cancelled';
  paymentMethod: string;
  downloadUrl?: string;
  paymentUrl?: string;
  planName?: string;
  planDescription?: string;
  discount?: number;
  taxes?: number;
}

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  cpf: string;
  company: string;
  cnpj: string;
  cep: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
}

type ProfileLike = {
  name?: string;
  email?: string;
  phone?: string;
  document?: string;
  cnpj?: string;
  company_name?: string;
  company_document?: string;
  address?: {
    cep?: string;
    zip_code?: string;
    street?: string;
    line1?: string;
    number?: string;
    complement?: string;
    line2?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  } | null;
};

const mapProfileToFormData = (profile: ProfileLike | null | undefined): ProfileFormData => {
  const address = profile?.address || {};
  return {
    name: profile?.name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    cpf: profile?.document || '',
    company: profile?.company_name || '',
    cnpj: profile?.cnpj || profile?.company_document || '',
    cep: address.cep || address.zip_code || '',
    street: address.street || address.line1 || '',
    number: address.number || '',
    complement: address.complement || address.line2 || '',
    neighborhood: address.neighborhood || '',
    city: address.city || '',
    state: address.state || '',
  };
};


const ACCOUNT_TABS = ['profile', 'security', 'billing', 'plan'] as const;

// Feature item component for plan features
const FeatureItem = ({
  text,
  enabled = true,
  highlight = false,
}: {
  text: string;
  enabled?: boolean;
  highlight?: boolean;
}) => (
  <li
    className={cn(
      'flex items-center gap-2 text-sm',
      !enabled && 'text-muted-foreground line-through'
    )}
  >
    {enabled ? (
      <CheckCircle2
        className={cn(
          'h-4 w-4 flex-shrink-0',
          highlight ? 'text-amber-500' : 'text-emerald-500'
        )}
      />
    ) : (
      <span className="h-4 w-4 flex-shrink-0 text-muted-foreground">✗</span>
    )}
    <span className={cn(
      highlight && enabled ? 'font-medium text-amber-700' : ''
    )}>
      {text}
    </span>
  </li>
);

/** Selos do topo dos cards ("SEU PLANO ATUAL", "POPULAR"): um só tamanho. O
 * `whitespace-nowrap` impede a quebra — o selo é absoluto a partir do centro do card. */
const SELO_DESTAQUE =
  'text-white whitespace-nowrap px-3 py-1 text-xs font-bold tracking-wide shadow-md';
const ICONE_SELO_DESTAQUE = 'h-3.5 w-3.5 mr-1.5';

/** Complemento do "Você está no plano X": deixa explícita a periodicidade da
 * cobrança, com o mesmo destaque do nome do plano — o gratuito não tem cobrança. */
function ResumoDaCobranca({ subscription }: Readonly<{ subscription: UserSubscription }>) {
  if (subscription.is_free_plan) return <>, gratuito.</>;
  // No teste ainda não há cobrança: a periodicidade é escolhida ao assinar.
  if (subscription.trial) {
    return (
      <>
        , em{' '}
        <span className="font-medium text-primary">Período de Teste</span>.
      </>
    );
  }
  const ciclo = subscription.billing_cycle === 'yearly' ? 'Anual' : 'Mensal';
  return (
    <>
      , com cobrança <span className="font-medium text-primary">{ciclo}</span>.
    </>
  );
}

export default function MyAccountPage() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // A aba vem da URL (?tab=…), e trocar de aba atualiza a URL: assim um link
  // de fora da página (ex.: "Assinar agora" do aviso do período de teste) abre
  // a aba certa mesmo com a conta já aberta.
  const tabFromUrl = searchParams.get('tab');
  const activeTab =
    tabFromUrl && ACCOUNT_TABS.includes(tabFromUrl as (typeof ACCOUNT_TABS)[number])
      ? tabFromUrl
      : 'profile';
  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams(
        (atuais) => {
          const proximos = new URLSearchParams(atuais);
          proximos.set('tab', tab);
          return proximos;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  
  // Profile state
  const [profileData, setProfileData] = useState<ProfileFormData>(() => mapProfileToFormData(user));
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  // Regra do backend (quantidade de usuários do plano), não o nome/tipo do plano.
  const allowsAccountMembers = Boolean(user?.subscription?.allows_account_members);
  
  // Email change state
  const [isEmailChangeDialogOpen, setIsEmailChangeDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [isEmailVerificationSent, setIsEmailVerificationSent] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  
  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // 2FA state - get initial value from user profile
  const [is2FAEnabled, setIs2FAEnabled] = useState(user?.is_2fa_enabled ?? false);
  const [allowAdminAccess, setAllowAdminAccess] = useState(user?.allow_admin_access ?? false);
  const [isUpdatingAdminAccess, setIsUpdatingAdminAccess] = useState(false);
  const [is2FASetupDialogOpen, setIs2FASetupDialogOpen] = useState(false);
  const [twoFASetupStep, setTwoFASetupStep] = useState<'qrcode' | 'verify'>('qrcode');
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQRCode, setTwoFAQRCode] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);
  
  // Invoices state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  
  // Plans state
  const [plans, setPlans] = useState<BackendPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  // Lida do cache compartilhado (não de uma cópia local): quando a
  // reconciliação do Layout confirma o pagamento de uma troca, a tela reflete
  // o novo plano sem precisar recarregar a página.
  const { data: currentSubscription = null, isPending: isSubscriptionPending } = useQuery({
    ...mySubscriptionQueryOptions,
    enabled: !!user,
  });
  // Regras decididas no backend: até quando o anual pago prende a assinatura e
  // se quem vê é o titular (membro convidado só consulta o plano).
  const annualLockUntil = currentSubscription?.annual_lock_until ?? null;
  const canManagePlan = currentSubscription?.is_billing_owner !== false;
  const planoGratuito = !!currentSubscription?.is_free_plan;
  const emTeste = currentSubscription?.status?.toLowerCase() === 'trial';
  // Cancelamento (chamado 7116): regra e datas vêm prontas do backend. Com ele
  // agendado no plano pago, o plano só segue até a data — sem trocas.
  const cancelamento = currentSubscription?.cancellation ?? null;
  const cancelamentoAgendado = !!cancelamento?.scheduled;
  const trocasBloqueadasPeloCancelamento = cancelamentoAgendado && !emTeste;
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<BackendPlan | null>(null);
  const [isChangePlanDialogOpen, setIsChangePlanDialogOpen] = useState(false);
  // Coleta de dados (CPF/endereço) ao escolher um plano pago durante o trial:
  // guarda o pedido para retomá-lo quando o cadastro for concluído.
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [pendingPaidChange, setPendingPaidChange] = useState<{
    target: PlanTarget;
    cycle: BillingCycle;
  } | null>(null);
  // Período de teste: situação e cotação vêm prontas do backend (chamado 7176).
  const trialSummary = currentSubscription?.trial ?? null;
  const assinaturaNoTesteAguardando = isTrialConversionPending(currentSubscription);
  const [isTrialSubscribeOpen, setIsTrialSubscribeOpen] = useState(false);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isCancellingPending, setIsCancellingPending] = useState(false);

  // Cancelamento da assinatura: o motivo e o comentário ficam no modal.
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);

  // Update 2FA state when user changes
  useEffect(() => {
    if (user) {
      setIs2FAEnabled(user.is_2fa_enabled ?? false);
      setAllowAdminAccess(user.allow_admin_access ?? false);
    }
  }, [user]);

  useEffect(() => {
    if (!isEditingProfile) {
      setProfileData(mapProfileToFormData(user));
    }
  }, [user, isEditingProfile]);
  
  // Fetch invoices when tab changes to billing — e de novo quando a assinatura
  // muda (ex.: pagamento da troca confirmado), para a fatura não ficar pendente.
  // Espera a assinatura carregar para não disparar duas buscas na abertura.
  useEffect(() => {
    if (activeTab === 'billing' && !isSubscriptionPending) {
      fetchInvoices();
    }
  }, [activeTab, currentSubscription, isSubscriptionPending]);
  
  const fetchPlansAndSubscription = useCallback(async () => {
    setIsLoadingPlans(true);
    try {
      // A assinatura é buscada de novo (staleTime 0) e gravada no cache
      // compartilhado, que alimenta esta tela e o Layout.
      const [plansData] = await Promise.all([
        planService.listPublicPlans(),
        queryClient.fetchQuery({ ...mySubscriptionQueryOptions, staleTime: 0 }),
      ]);
      setPlans([...plansData].sort((a, b) => a.price_monthly - b.price_monthly));
    } catch (error) {
      console.error('Erro ao buscar planos:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os planos.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPlans(false);
    }
  }, [queryClient]);

  // Fetch plans and subscription when tab changes to plan
  useEffect(() => {
    if (activeTab === 'plan') {
      fetchPlansAndSubscription();
    }
  }, [activeTab, fetchPlansAndSubscription]);

  // Só a busca mais recente publica: uma resposta antiga que chegue por último
  // não pode trazer de volta a fatura "Aguardando pagamento" já paga.
  const latestInvoicesRequest = useRef(0);

  const fetchInvoices = async () => {
    const requestId = ++latestInvoicesRequest.current;
    const isLatest = () => requestId === latestInvoicesRequest.current;
    setIsLoadingInvoices(true);
    try {
      const data = await accountService.getMyInvoices();
      if (!isLatest()) return;
      const formattedInvoices: Invoice[] = data.map((inv: AccountInvoice) => ({
        id: inv.id,
        number: inv.invoice_number,
        date: inv.created_at || '',
        dueDate: inv.due_date || '',
        amount: inv.amount,
        status: inv.status,
        paymentMethod:
          inv.status === 'paid'
            ? 'Pago'
            : inv.status === 'cancelled'
              ? 'Cancelada'
              : inv.status === 'overdue'
                ? 'Em atraso'
                : 'Aguardando pagamento',
        downloadUrl: inv.pdf_url,
        paymentUrl: inv.payment_url,
        planName: inv.plan_name || 'Plano',
        planDescription: inv.description || `Período: ${inv.period_start || ''} a ${inv.period_end || ''}`,
      }));
      setInvoices(formattedInvoices);
    } catch (error) {
      if (!isLatest()) return;
      console.error('Erro ao buscar faturas:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as faturas.',
        variant: 'destructive',
      });
    } finally {
      if (isLatest()) setIsLoadingInvoices(false);
    }
  };
  const [isDisable2FADialogOpen, setIsDisable2FADialogOpen] = useState(false);
  const [disable2FACode, setDisable2FACode] = useState('');
  
  // Invoice state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isInvoiceDetailDialogOpen, setIsInvoiceDetailDialogOpen] = useState(false);
  
  // Handlers
  const handleToggleProfileEdit = () => {
    if (isEditingProfile) {
      setProfileData(mapProfileToFormData(user));
    }
    setIsEditingProfile((prev) => !prev);
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const updatedProfile = await userService.updateMyProfile({
        name: profileData.name,
        phone: profileData.phone,
        document: profileData.cpf,
        cnpj: profileData.cnpj,
        company_document: profileData.cnpj,
        company_name: profileData.company,
        cep: profileData.cep,
        street: profileData.street,
        number: profileData.number,
        complement: profileData.complement,
        neighborhood: profileData.neighborhood,
        city: profileData.city,
        state: profileData.state,
      });

      const subscriberCompletion = getSubscriberProfileCompletion(updatedProfile);
      if (updatedProfile.user_type === 'subscriber' && !subscriberCompletion.requiresCompletion) {
        await userService.syncMySubscriberProfileWithVio();
      }

      setProfileData(mapProfileToFormData(updatedProfile));
      setIsEditingProfile(false);
      await refreshProfile();
      toast({ title: 'Perfil atualizado com sucesso!' });
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Não foi possível salvar os dados do perfil.';
      toast({ title: 'Erro ao atualizar perfil', description: message, variant: 'destructive' });
    } finally {
      setIsSavingProfile(false);
    }
  };
  
  const handleEmailChange = () => {
    setNewEmail(profileData.email);
    setIsEmailChangeDialogOpen(true);
  };
  
  const handleSendEmailVerification = async () => {
    if (!newEmail || newEmail === profileData.email) {
      toast({ title: 'Digite um novo e-mail diferente do atual', variant: 'destructive' });
      return;
    }
    
    setIsSendingEmailCode(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSendingEmailCode(false);
    setIsEmailVerificationSent(true);
    toast({ title: 'Código de verificação enviado!', description: `Verifique sua caixa de entrada em ${newEmail}` });
  };
  
  const handleVerifyEmailCode = async () => {
    if (emailVerificationCode.length !== 6) {
      toast({ title: 'Digite o código completo de 6 dígitos', variant: 'destructive' });
      return;
    }
    
    setIsVerifyingEmail(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock verification (accept any 6-digit code for demo)
    setProfileData({ ...profileData, email: newEmail });
    setIsVerifyingEmail(false);
    setIsEmailChangeDialogOpen(false);
    setIsEmailVerificationSent(false);
    setNewEmail('');
    setEmailVerificationCode('');
    toast({ title: 'E-mail alterado com sucesso!' });
  };
  
  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: 'As senhas não conferem', variant: 'destructive' });
      return;
    }
    if (passwordData.newPassword.length < 8) {
      toast({ title: 'A nova senha deve ter pelo menos 8 caracteres', variant: 'destructive' });
      return;
    }
    
    setIsChangingPassword(true);
    try {
      await accountService.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast({ title: 'Senha alterada com sucesso!' });
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Erro ao alterar senha';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setIsChangingPassword(false);
    }
  };
  
  const handleSetup2FA = async () => {
    setTwoFASetupStep('qrcode');
    setTwoFACode('');
    setIs2FASetupDialogOpen(true);
    
    try {
      const response = await accountService.setup2FA();
      setTwoFASecret(response.secret);
      setTwoFAQRCode(response.qr_code);
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Erro ao configurar 2FA';
      toast({ title: message, variant: 'destructive' });
      setIs2FASetupDialogOpen(false);
    }
  };
  
  const handleVerify2FASetup = async () => {
    if (twoFACode.length !== 6) {
      toast({ title: 'Digite o código completo de 6 dígitos', variant: 'destructive' });
      return;
    }
    
    setIsVerifying2FA(true);
    try {
      await accountService.enable2FA(twoFACode);
      setIs2FAEnabled(true);
      setIs2FASetupDialogOpen(false);
      setTwoFACode('');
      await refreshProfile();
      toast({ title: 'Autenticação de dois fatores ativada!' });
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Código inválido';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setIsVerifying2FA(false);
    }
  };
  
  const handleDisable2FA = async () => {
    if (disable2FACode.length !== 6) {
      toast({ title: 'Digite o código completo de 6 dígitos', variant: 'destructive' });
      return;
    }
    
    setIsVerifying2FA(true);
    try {
      await accountService.disable2FA(passwordData.currentPassword || 'password', disable2FACode);
      setIs2FAEnabled(false);
      setIsDisable2FADialogOpen(false);
      setDisable2FACode('');
      await refreshProfile();
      toast({ title: 'Autenticação de dois fatores desativada' });
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Erro ao desativar 2FA';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setIsVerifying2FA(false);
    }
  };

  const handleAdminAccessPermissionToggle = async (checked: boolean) => {
    setIsUpdatingAdminAccess(true);
    try {
      const response = await accountService.updateAdminAccessPermission(checked);
      setAllowAdminAccess(response.allow_admin_access);
      await refreshProfile();
      toast({
        title: response.allow_admin_access
          ? 'Permissão de acesso ativada'
          : 'Permissão de acesso desativada',
        description: response.allow_admin_access
          ? 'Admins poderão acessar sua conta pelo Painel Administrativo.'
          : 'Admins não poderão mais acessar sua conta pelo Painel Administrativo.',
      });
    } catch (error: unknown) {
      const message = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail || 'Não foi possível atualizar a permissão de acesso.';
      toast({
        title: 'Erro ao atualizar permissão',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingAdminAccess(false);
    }
  };

  // Executa a troca de plano de fato (change-plan in-place na VIO Pay). Separado
  // de handleChangePlan para poder ser retomado após a coleta de dados do plano
  // pago, sem re-checar o perfil (evita pitfall de closure com `user` stale).
  const executePaidChange = async (
    target: PlanTarget | null = selectedPlan,
    cycle: BillingCycle = billingCycle,
  ) => {
    if (!target) return;
    // No teste, pedir o próprio plano é assinar (abre a cobrança da assinatura).
    const assinandoNoTeste = !!trialSummary && target.id === currentSubscription?.plan_id;
    setIsChangingPlan(true);
    try {
      // Endpoint dedicado de troca de plano: cancela faturas em aberto, edita
      // a assinatura in-place na VIO Pay (mesma `vio_subscription_uuid`,
      // incluindo periodicidade) e gera manualmente a nova cobrança, devolvendo
      // a `payment_url` do checkout. Nunca cria nova assinatura por trás.
      const result = await accountService.changeMyPlan({
        plan_id: target.id,
        billing_cycle: cycle,
      });

      await refreshProfile();
      await fetchPlansAndSubscription();
      // A plataforma não sai desta aba. Ao ver a troca pendente, o Layout abre o
      // PendingPlanChangeModal, cujo "Realize o pagamento" leva ao checkout em
      // nova aba no clique do usuário (abrir daqui, após o await, cairia no
      // bloqueador de pop-ups), e passa a acompanhar a confirmação do pagamento.
      await refreshSubscriptionQueries(queryClient);

      if (result.status === 'scheduled' && result.effective_date) {
        // Ida ao gratuito no teste: fica para o fim dele (chamado 7116).
        toast({ title: 'Mudança agendada', description: freePlanScheduledText(result.effective_date) });
      } else if (result.status === 'pending_payment' && assinandoNoTeste) {
        toast({
          title: 'Falta só o pagamento',
          description:
            'Clique em "Realize o pagamento" para pagar em uma nova aba. Sua assinatura é ativada assim que o pagamento for confirmado.',
        });
      } else if (result.status === 'pending_payment') {
        // O plano só é liberado após o pagamento. Com checkout, o aviso aponta
        // o botão que o abre (modal do Layout ou card do plano pendente) — o
        // modal não reabre se a pendência já existia para o mesmo plano.
        toast({
          title: `Troca registrada: ${target.name}`,
          description: result.payment_url
            ? 'Clique em "Realize o pagamento" para pagar em uma nova aba. O novo plano é liberado após a confirmação.'
            : 'O novo plano é liberado após a confirmação do pagamento da fatura.',
        });
      } else if (result.status === 'pending_cancelled') {
        // Pediu o próprio plano vigente com uma troca pendente aberta: o
        // backend cancelou a pendência (voltar atrás), não trocou de plano.
        toast({
          title: 'Troca de plano cancelada',
          description: 'Você segue no seu plano atual.',
        });
      } else {
        toast({
          title: `Plano alterado: ${target.name}`,
          description:
            'Sua assinatura foi atualizada na VIO Pay. A próxima cobrança seguirá o novo valor.',
        });
      }

      setIsChangePlanDialogOpen(false);
      setIsTrialSubscribeOpen(false);
      setSelectedPlan(null);
    } catch (error: unknown) {
      const message =
        (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
        'Não foi possível alterar o plano. Tente novamente em instantes.';
      toast({ title: 'Erro ao alterar plano', description: message, variant: 'destructive' });
    } finally {
      setIsChangingPlan(false);
    }
  };

  // Troca pendente de pagamento: descarta o alvo no backend e o assinante
  // segue no plano vigente (as cobranças do upgrade são canceladas lá).
  const handleCancelPendingChange = async () => {
    const eraAssinaturaNoTeste = assinaturaNoTesteAguardando;
    // Lido antes de desistir: depois da resposta a assinatura já é outra.
    const avisoDaDesistencia = currentSubscription ? trialExitToast(currentSubscription) : '';
    setIsCancellingPending(true);
    try {
      await accountService.cancelPendingPlanChange();
      await fetchPlansAndSubscription();
      // Sem cache obsoleto, o modal de pendência não reaparece.
      await refreshSubscriptionQueries(queryClient);
      toast(
        eraAssinaturaNoTeste
          ? { title: 'Tudo certo', description: avisoDaDesistencia }
          : { title: 'Troca de plano cancelada', description: 'Você segue no seu plano atual.' },
      );
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      const message =
        detail ||
        'Não foi possível cancelar a troca de plano. Tente novamente em instantes.';
      toast({ title: 'Erro ao cancelar a troca', description: message, variant: 'destructive' });
    } finally {
      setIsCancellingPending(false);
    }
  };

  // Cobrança em aberto da troca/assinatura pendente: abre o checkout em nova
  // aba (preserva a sessão); sem link ainda, leva às faturas.
  const handlePayPending = () => payPendingCharge(currentSubscription?.pending_checkout_url, navigate);

  // "Assinar agora" no teste: o próprio plano, no ciclo escolhido. Sem os dados
  // de cobrança (CPF/endereço), coleta antes e retoma o pedido em seguida.
  const handleTrialSubscribe = async (cycle: BillingCycle) => {
    if (!currentSubscription) return;
    const target: PlanTarget = {
      id: currentSubscription.plan_id,
      name: currentSubscription.plan_name ?? 'seu plano',
    };
    if (getSubscriberProfileCompletion(user).requiresCompletion) {
      setIsTrialSubscribeOpen(false);
      setPendingPaidChange({ target, cycle });
      setShowProfileCompletion(true);
      return;
    }
    await executePaidChange(target, cycle);
  };

  // Selo do card do plano vigente: no teste diz a situação real — o plano
  // ainda não foi assinado.
  const SeloDoPlanoAtualIcone = trialSummary ? Clock : Crown;
  const seloDoPlanoAtualTexto = trialSummary ? 'PERÍODO DE TESTE' : 'SEU PLANO ATUAL';

  // CTA do card do plano vigente: no teste é assinar (a periodicidade é
  // escolhida na confirmação); fora dele, o selo já identifica o plano e a
  // única ação é migrar a periodicidade (planos pagos).
  const renderCtaDoPlanoAtual = (plan: BackendPlan, isFree: boolean) => {
    if (!currentSubscription || !canManagePlan || trocasBloqueadasPeloCancelamento) return null;
    if (trialSummary) {
      if (assinaturaNoTesteAguardando) return null;
      return (
        <Button
          className="w-full"
          onClick={() => setIsTrialSubscribeOpen(true)}
          disabled={!canSubscribeInTrial(currentSubscription)}
        >
          Assinar agora
        </Button>
      );
    }
    if (isFree) return null;
    return (
      <CurrentPlanCycleAction
        subscription={currentSubscription}
        onMigrate={(targetCycle) => {
          setSelectedPlan(plan);
          setBillingCycle(targetCycle);
          setIsChangePlanDialogOpen(true);
        }}
      />
    );
  };

  // CTA dos cards de plano que NÃO são o plano atual: com troca pendente
  // aguardando pagamento, o card do alvo oferece pagar/cancelar; nos demais,
  // o botão padrão de assinar/upgrade/downgrade.
  // No teste ainda em curso, ir ao gratuito fica para o fim dele (o backend
  // agenda); a confirmação não fala em perder os dias restantes.
  const gratuitoNoFimDoTeste = (alvoPago: boolean) =>
    !alvoPago && !!trialSummary && !trialSummary.has_ended;
  const avisoDaTroca = (alvoPago: boolean, alvo: string): string => {
    if (alvoPago) {
      return 'O novo plano é liberado assim que o pagamento for confirmado. Até lá, você continua com os recursos do plano atual.';
    }
    if (gratuitoNoFimDoTeste(alvoPago) && trialSummary) {
      return trialFreePlanChangeNotice(
        trialSummary.trial_end,
        currentSubscription?.plan_name ?? 'plano atual',
        alvo,
      );
    }
    return 'A mudança para o plano gratuito é imediata e não gera cobrança.';
  };

  const renderCtaDePlanoNaoAtual = (
    plan: BackendPlan,
    isPendingPlan: boolean,
    isPopular: boolean,
    isFree: boolean,
    monthlyPrice: number,
  ) => {
    // Só o titular troca de plano: o membro convidado apenas consulta. Com o
    // cancelamento agendado no pago, o plano só segue até a data — sem trocas.
    if (!canManagePlan || trocasBloqueadasPeloCancelamento) return null;
    // No teste com a ida ao gratuito já agendada, o gratuito não é pedido de novo.
    if (isFree && cancelamentoAgendado) return null;

    if (isPendingPlan) {
      return (
        <div className="space-y-2">
          {/* Troca pedida e aguardando pagamento: o plano só é liberado
              quando a cobrança for paga. */}
          <Button
            className="w-full bg-amber-600 hover:bg-amber-700"
            disabled={isCancellingPending}
            onClick={handlePayPending}
          >
            Realize o pagamento
          </Button>
          <Button
            variant="outline"
            className="w-full"
            disabled={isCancellingPending}
            onClick={handleCancelPendingChange}
          >
            {isCancellingPending ? 'Cancelando...' : 'Cancelar troca'}
          </Button>
        </div>
      );
    }

    let rotulo: string;
    if (!currentSubscription) {
      rotulo = isFree ? 'Começar grátis' : 'Assinar';
    } else {
      const precoAtual =
        plans.find((p) => p.id === currentSubscription?.plan_id)?.price_monthly || 0;
      rotulo = monthlyPrice > precoAtual ? 'Fazer Upgrade' : 'Fazer Downgrade';
    }

    // Anual pago vigente: o gratuito só fica disponível ao fim da vigência.
    const bloqueio = isFree && annualLockUntil ? annualLockToPlanMessage(plan.name, annualLockUntil) : null;

    return (
      <LockedActionHint message={bloqueio}>
        <Button
          className={cn('w-full', isPopular && 'bg-amber-500 hover:bg-amber-600')}
          onClick={() => {
            setSelectedPlan(plan);
            // Com o anual vigente o mensal está bloqueado: a troca abre no anual.
            setBillingCycle(annualLockUntil ? 'yearly' : 'monthly');
            setIsChangePlanDialogOpen(true);
          }}
        >
          {rotulo}
        </Button>
      </LockedActionHint>
    );
  };

  const handleChangePlan = async () => {
    if (!selectedPlan) return;

    // Onboarding inicial (sem subscription ativa) segue pelo fluxo legado de
    // checkout (/register/checkout coleta CPF/endereço/cartão). Assinatura ATIVA
    // (inclui trial) passa pelo change-plan in-place na VIO Pay.
    const hasActiveSubscription = !!currentSubscription?.is_active;
    if (!hasActiveSubscription) {
      setIsChangePlanDialogOpen(false);
      setSelectedPlan(null);
      navigate('/register/checkout', {
        state: {
          name: user?.name,
          email: user?.email,
          plan: selectedPlan,
          billingCycle,
          accountType: selectedPlan.account_type || 'individual',
        },
      });
      return;
    }

    // Plano pago exige dados de cobrança (CPF/CNPJ + endereço). Durante o trial
    // esses dados não foram coletados; se faltarem, coletamos AGORA (modal) e só
    // então geramos a cobrança na VIO — o onCompleted retoma executePaidChange.
    const isPaidPlan = (selectedPlan.price_monthly ?? 0) > 0;
    if (isPaidPlan && getSubscriberProfileCompletion(user).requiresCompletion) {
      setIsChangePlanDialogOpen(false);
      setPendingPaidChange({ target: selectedPlan, cycle: billingCycle });
      setShowProfileCompletion(true);
      return;
    }

    await executePaidChange();
  };

  // No plano gratuito não há assinatura a cancelar: o botão só informa, sem
  // abrir o modal (mesmo texto do 409 do backend).
  const handleOpenCancelSubscription = () => {
    if (planoGratuito) {
      toast({ title: 'Plano Gratuito', description: FREE_PLAN_CANCEL_MESSAGE, variant: 'destructive' });
      return;
    }
    setIsCancelDialogOpen(true);
  };

  // Cancelar é só cancelar a assinatura: a pessoa segue logada, com os
  // recursos até a data efetiva, e a tela passa a mostrar o agendamento.
  const handleCancelSubscription = async (
    reasonCategory: CancellationReason,
    reason: string | undefined,
  ) => {
    setIsCancellingSubscription(true);
    try {
      const resultado = await accountService.cancelMySubscription(reasonCategory, reason);
      setIsCancelDialogOpen(false);
      await fetchPlansAndSubscription();
      await refreshSubscriptionQueries(queryClient);
      toast(
        resultado.cancellation_scheduled && resultado.effective_date
          ? { title: 'Cancelamento agendado', description: cancellationScheduledText(resultado.effective_date) }
          : { title: 'Assinatura cancelada', description: resultado.message },
      );
    } catch (error: unknown) {
      const message =
        (error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
        'Não foi possível cancelar a assinatura. Tente novamente em instantes.';
      toast({
        title: 'Erro ao cancelar assinatura',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsInvoiceDetailDialogOpen(true);
  };
  
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };
    const labels = { paid: 'Pago', pending: 'Pendente', overdue: 'Atrasado', cancelled: 'Cancelado' };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiado para a área de transferência!' });
  };
  
  // Get QR Code URL - use the one from backend or generate fallback
  const getQRCodeUrl = () => {
    if (twoFAQRCode) {
      // If backend returns a data URL or URL
      return twoFAQRCode.startsWith('data:') ? twoFAQRCode : `data:image/png;base64,${twoFAQRCode}`;
    }
    // Fallback to generating via API
    const otpAuthUrl = `otpauth://totp/Librattum:${profileData.email}?secret=${twoFASecret}&issuer=Librattum`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpAuthUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full px-6 py-8 space-y-8">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground flex items-center gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Settings className="h-6 w-6 text-primary" />
              </div>
              Minha Conta
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie suas informações pessoais, segurança, faturas e plano
            </p>
          </div>
        </header>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card p-1 shadow-sm border border-border/60">
            <TabsTrigger value="profile" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Perfil</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Lock className="h-4 w-4" />
              <span className="hidden sm:inline">Segurança</span>
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Receipt className="h-4 w-4" />
              <span className="hidden sm:inline">Faturas</span>
            </TabsTrigger>
            <TabsTrigger value="plan" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-white">
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">Plano</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card className="border-border/60">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Informações Pessoais
                  </CardTitle>
                  <CardDescription>
                    Atualize seus dados de cadastro
                  </CardDescription>
                </div>
                <Button
                  variant={isEditingProfile ? "outline" : "default"}
                  onClick={handleToggleProfileEdit}
                >
                  {isEditingProfile ? 'Cancelar' : 'Editar'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Info */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Dados Pessoais
                    </h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="name">Nome Completo</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="name"
                          value={profileData.name}
                          onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                          disabled={!isEditingProfile}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="email"
                          type="email"
                          value={profileData.email}
                          disabled
                          className="pl-10 bg-muted/50"
                        />
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 text-xs"
                          onClick={handleEmailChange}
                        >
                          Alterar
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">Para alterar o e-mail, você receberá um código de verificação</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="phone"
                          value={profileData.phone}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          disabled={!isEditingProfile}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cpf">CPF</Label>
                      <Input
                        id="cpf"
                        value={profileData.cpf}
                        onChange={(e) => setProfileData({ ...profileData, cpf: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                      <p className="text-xs text-muted-foreground">Preencha o CPF quando este for o documento principal do cadastro</p>
                    </div>
                  </div>
                  
                  {/* Company Info */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                      Dados da Empresa
                    </h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company">Razão Social</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="company"
                          value={profileData.company}
                          onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                          disabled={!isEditingProfile}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cnpj">CNPJ</Label>
                      <Input
                        id="cnpj"
                        value={profileData.cnpj}
                        onChange={(e) => setProfileData({ ...profileData, cnpj: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                      <p className="text-xs text-muted-foreground">Preencha o CNPJ quando o cadastro for de pessoa jurídica</p>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                {/* Address */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Endereço
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cep">CEP</Label>
                      <Input
                        id="cep"
                        value={profileData.cep}
                        onChange={(e) => setProfileData({ ...profileData, cep: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="street">Logradouro</Label>
                      <Input
                        id="street"
                        value={profileData.street}
                        onChange={(e) => setProfileData({ ...profileData, street: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="number">Número</Label>
                      <Input
                        id="number"
                        value={profileData.number}
                        onChange={(e) => setProfileData({ ...profileData, number: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="complement">Complemento</Label>
                      <Input
                        id="complement"
                        value={profileData.complement}
                        onChange={(e) => setProfileData({ ...profileData, complement: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro</Label>
                      <Input
                        id="neighborhood"
                        value={profileData.neighborhood}
                        onChange={(e) => setProfileData({ ...profileData, neighborhood: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade</Label>
                      <Input
                        id="city"
                        value={profileData.city}
                        onChange={(e) => setProfileData({ ...profileData, city: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">Estado</Label>
                      <Input
                        id="state"
                        value={profileData.state}
                        onChange={(e) => setProfileData({ ...profileData, state: e.target.value })}
                        disabled={!isEditingProfile}
                      />
                    </div>
                  </div>
                </div>
                
                {isEditingProfile && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                      {isSavingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Salvar Alterações
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {allowsAccountMembers && (
              <AccountMembersCard enabled={activeTab === 'profile'} />
            )}

            {/* Só o titular vê (regra do backend) — chamado 7116. */}
            <DeleteAccountCard enabled={activeTab === 'profile'} />
          </TabsContent>
          
          {/* Security Tab */}
          <TabsContent value="security" className="space-y-6">
            {/* Password Change */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5 text-primary" />
                  Alterar Senha
                </CardTitle>
                <CardDescription>
                  Mantenha sua conta segura com uma senha forte
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 max-w-md">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Senha Atual</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="currentPassword"
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      className="pl-10 pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                    >
                      {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Nova Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="newPassword"
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      className="pl-10 pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                    >
                      {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      className="pl-10 pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                    >
                      {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                
                {/* Password requirements */}
                <div className="bg-muted/30 rounded-lg p-4 space-y-2 border border-border/40">
                  <p className="text-sm font-medium text-foreground">Requisitos da senha:</p>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className={cn("flex items-center gap-2", passwordData.newPassword.length >= 8 && "text-emerald-600")}>
                      {passwordData.newPassword.length >= 8 ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      Mínimo de 8 caracteres
                    </li>
                    <li className={cn("flex items-center gap-2", /[A-Z]/.test(passwordData.newPassword) && "text-emerald-600")}>
                      {/[A-Z]/.test(passwordData.newPassword) ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      Uma letra maiúscula
                    </li>
                    <li className={cn("flex items-center gap-2", /\d/.test(passwordData.newPassword) && "text-emerald-600")}>
                      {/\d/.test(passwordData.newPassword) ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                      Um número
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  className="w-full"
                >
                  {isChangingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Alterar Senha
                </Button>
              </CardContent>
            </Card>
            
            {/* Two-Factor Authentication */}
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Autenticação em Duas Etapas (2FA)
                </CardTitle>
                <CardDescription>
                  Adicione uma camada extra de segurança à sua conta
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/40">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-xl",
                      is2FAEnabled ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted"
                    )}>
                      <Smartphone className={cn(
                        "h-6 w-6",
                        is2FAEnabled ? "text-emerald-600" : "text-muted-foreground"
                      )} />
                    </div>
                    <div>
                      <p className="font-medium">Autenticação por App</p>
                      <p className="text-sm text-muted-foreground">
                        {is2FAEnabled 
                          ? 'Use um aplicativo autenticador para acessar sua conta' 
                          : 'Configure um aplicativo autenticador como Google Authenticator ou Authy'}
                      </p>
                    </div>
                  </div>
                  
                  {is2FAEnabled ? (
                    <div className="flex items-center gap-3">
                      <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Ativo
                      </Badge>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsDisable2FADialogOpen(true)}
                      >
                        Desativar
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={handleSetup2FA}>
                      Configurar
                    </Button>
                  )}
                </div>
                
                {/* 2FA Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium mb-1">Por que usar autenticação de dois fatores?</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300">
                        <li>Protege sua conta mesmo se sua senha for comprometida</li>
                        <li>Impede acessos não autorizados de dispositivos desconhecidos</li>
                        <li>Adiciona uma verificação extra ao fazer login</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {user?.user_type === 'subscriber' && (
              <Card className="border-border/60">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Permissão de Acesso
                  </CardTitle>
                  <CardDescription>
                    Gerencie o acesso do Administrador ao seu painel Librattum.
                    <br />
                    Ative para permitir o acesso ou desative para bloquear.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border/40">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Permitir acesso do Administrador</span>
                        <span className={cn(
                          "inline-block h-2.5 w-2.5 rounded-full",
                          allowAdminAccess ? "bg-emerald-500" : "bg-gray-400"
                        )} />
                        <Badge className={cn(
                          allowAdminAccess
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                        )}>
                          {allowAdminAccess ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground max-w-2xl">
                        Ao ativar, o Administrador poderá acessar sua conta pelo Painel Administrativo.
                      </p>
                    </div>

                    <Switch
                      checked={allowAdminAccess}
                      onCheckedChange={handleAdminAccessPermissionToggle}
                      disabled={isUpdatingAdminAccess}
                      aria-label="Permitir acesso do Administrador à minha conta"
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card className="border-border/60">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-primary" />
                  Histórico de Faturas
                </CardTitle>
                <CardDescription>
                  Visualize e baixe suas faturas anteriores
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  if (isLoadingInvoices) return (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      </div>
                  );
                  if (invoices.length === 0) return (
                      <div className="text-center py-12">
                        <Receipt className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground">Nenhuma fatura encontrada</p>
                      </div>
                  );
                  return (
                    <div className="space-y-4">
                      {invoices.map((invoice) => (
                        <div
                          key={invoice.id}
                          className={cn(
                            "flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl transition-colors border",
                            invoice.status === 'pending' || invoice.status === 'overdue'
                              ? "bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800"
                              : "bg-muted/30 hover:bg-muted/50 border-border/40"
                          )}
                        >
                          <div className="flex items-center gap-4 mb-3 sm:mb-0">
                            <div className={cn(
                              "p-2 rounded-lg shadow-sm border",
                              invoice.status === 'pending' || invoice.status === 'overdue'
                                ? "bg-amber-100 dark:bg-amber-900/50 border-amber-200 dark:border-amber-700"
                                : "bg-background border-border/40"
                            )}>
                              <Receipt className={cn(
                                "h-5 w-5",
                                invoice.status === 'pending' || invoice.status === 'overdue'
                                  ? "text-amber-600 dark:text-amber-400"
                                  : "text-primary"
                              )} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{invoice.number}</p>
                                {getStatusBadge(invoice.status)}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {formatBrasilia(invoice.date, "dd 'de' MMMM 'de' yyyy")}
                              </p>
                              {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                                  Vencimento: {formatBrasilia(invoice.dueDate, "dd/MM/yyyy")}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="text-right">
                              <p className="font-semibold">
                                {formatBRL(invoice.amount)}
                              </p>
                              <p className="text-xs text-muted-foreground">{invoice.paymentMethod}</p>
                            </div>
                        
                            <div className="flex items-center gap-1">
                              {/* View Invoice Button */}
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="gap-1.5"
                                onClick={() => handleViewInvoice(invoice)}
                              >
                                <Eye className="h-4 w-4" />
                                <span className="hidden sm:inline">Visualizar</span>
                              </Button>
                          
                              {/* Download Button */}
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Plan Tab */}
          <TabsContent value="plan" className="space-y-6">
            {isLoadingPlans ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                {/* Current Plan */}
                {currentSubscription && (
                  <Card className="border-border/60 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Crown className="h-5 w-5 text-primary" />
                            Plano Atual
                            {/* No teste, o selo fica colado ao título para dar destaque. */}
                            {trialSummary && (
                              <Badge className="bg-amber-500 text-white">Período de teste</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>
                            Você está no plano <span className="font-medium text-primary">{currentSubscription.plan_name}</span>
                            <ResumoDaCobranca subscription={currentSubscription} />
                          </CardDescription>
                        </div>
                        {!trialSummary && (
                          <Badge className={cn(
                            currentSubscription.is_active ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"
                          )}>
                            {currentSubscription.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        const currentPlan = plans.find(p => p.id === currentSubscription.plan_id);
                        if (!currentPlan) return null;
                        // No anual, o preço é o da cobrança (por ano), com o equivalente
                        // mensal de apoio — "cobrança anual" com "/mês" confundia.
                        const anual = currentSubscription.billing_cycle === 'yearly';
                        return (
                          <>
                            <div className={cn(anual ? 'mb-1' : 'mb-4', 'flex items-baseline gap-1')}>
                              <span className="text-4xl font-bold">
                                {formatBRL(anual ? currentPlan.price_yearly : currentPlan.price_monthly)}
                              </span>
                              <span className="text-muted-foreground">{anual ? '/ano' : '/mês'}</span>
                            </div>
                            {anual && (
                              <p className="mb-4 text-sm text-muted-foreground">
                                {`equivale a ${formatBRL(currentPlan.price_yearly / MESES_NO_ANO)}/mês`}
                              </p>
                            )}
                            {currentPlan.plan_features && currentPlan.plan_features.length > 0 && (
                              <div className="grid grid-cols-2 gap-2">
                                {currentPlan.plan_features.filter(f => f.is_enabled).slice(0, 6).map((feature) => (
                                  <div key={feature.id} className="flex items-center gap-2 text-sm">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                    {feature.name}
                                  </div>
                                ))}
                              </div>
                            )}
                            {currentSubscription.current_period_end && !planoGratuito && !trialSummary && !cancelamentoAgendado && (
                              <p className="text-sm text-muted-foreground mt-4">
                                Próxima renovação: {formatBrasilia(currentSubscription.current_period_end, "dd 'de' MMMM 'de' yyyy")}
                              </p>
                            )}
                          </>
                        );
                      })()}
                      {cancelamentoAgendado && cancelamento && scheduledNoticeText(cancelamento) && (
                        <p className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
                          <CalendarClock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                          <span>{scheduledNoticeText(cancelamento)}</span>
                        </p>
                      )}
                      <TrialPlanStatus
                        subscription={currentSubscription}
                        canManage={canManagePlan}
                        keepingTrial={isCancellingPending}
                        onSubscribe={() => setIsTrialSubscribeOpen(true)}
                        onPay={handlePayPending}
                        onKeepTrial={handleCancelPendingChange}
                      />
                    </CardContent>
                  </Card>
                )}
                
                {!currentSubscription && (
                  <Card className="border-border/60 border-amber-300/50 bg-gradient-to-br from-amber-50/50 to-transparent">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-500" />
                        <div>
                          <CardTitle className="text-lg">Sem assinatura ativa</CardTitle>
                          <CardDescription>
                            Escolha um plano abaixo para começar a usar todos os recursos.
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                )}

                {/* Available Plans */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-7xl mx-auto">
                  {plans.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <Shield className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">
                        Nenhum plano disponível no momento.
                      </p>
                    </div>
                  ) : (
                    plans.map((plan) => {
                      const isCurrentPlan = currentSubscription?.plan_id === plan.id;
                      const isPendingPlan = currentSubscription?.pending_plan_id === plan.id;
                      const isPopular = plan.is_popular || plan.type === 'pro';
                      const monthlyPrice = plan.price_monthly;
                      const yearlyMonthlyPrice = plan.price_yearly / 12;
                      const isFree = plan.price_monthly === 0;
                      const yearlyDiscount = yearlyDiscountPercent(plan.price_monthly, plan.price_yearly);

                      return (
                        <Card
                          key={plan.id}
                          className={cn(
                            // A partir de 2 colunas, as quatro faixas do card (nome e
                            // descrição, preço, recursos, botão) seguem as linhas da grade:
                            // uma descrição longa não desalinha o preço dos outros cards.
                            "relative flex flex-col sm:grid sm:grid-rows-subgrid sm:row-span-4 sm:gap-0 border transition-all duration-200 hover:shadow-md",
                            isCurrentPlan
                              ? "border-primary/60 shadow-sm ring-1 ring-primary/20"
                              : isPopular
                                ? "border-amber-300/70 hover:border-amber-400"
                                : "border-border/60 hover:border-primary/30"
                          )}
                        >
                          {/* Badge (Atual / Aguardando pagamento / Popular) */}
                          {(isCurrentPlan || isPendingPlan || isPopular) && (
                            <div className={cn('absolute left-1/2 -translate-x-1/2 z-10', isPendingPlan && !isCurrentPlan ? '-top-2.5' : '-top-3.5')}>
                              {isCurrentPlan && (
                                // Indicador visual, não clicável. No teste, o selo diz a
                                // situação real: o plano ainda não foi assinado.
                                <Badge className={cn('bg-primary', SELO_DESTAQUE)}>
                                  <SeloDoPlanoAtualIcone className={ICONE_SELO_DESTAQUE} />
                                  {seloDoPlanoAtualTexto}
                                </Badge>
                              )}
                              {!isCurrentPlan && isPendingPlan && (
                                // `whitespace-nowrap`: o selo é absoluto a partir do centro
                                // do card e, sem isso, o texto longo quebra e invade o título.
                                <Badge className="bg-amber-600 text-white whitespace-nowrap px-2 py-0.5 text-[9px] leading-none font-semibold shadow-sm">
                                  <CreditCard className="h-2.5 w-2.5 mr-1" />
                                  AGUARDANDO PAGAMENTO
                                </Badge>
                              )}
                              {!isCurrentPlan && !isPendingPlan && (
                                <Badge className={cn('bg-amber-500', SELO_DESTAQUE)}>
                                  <Sparkles className={ICONE_SELO_DESTAQUE} />
                                  POPULAR
                                </Badge>
                              )}
                            </div>
                          )}

                          {/* Nome + descrição */}
                          <div className="px-5 pt-5 text-center">
                            <h3 className="text-base font-semibold leading-tight">{plan.name}</h3>
                            {plan.description && (
                              // Texto do admin completo (até 255 caracteres), sem cortar.
                              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground break-words min-h-[32px]">
                                {plan.description}
                              </p>
                            )}
                          </div>

                          {/* Preço */}
                          <div className="px-5 pb-4 text-center">
                            <div className="mt-4">
                              {isFree ? (
                                <div className="text-3xl font-bold">Grátis</div>
                              ) : (
                                <>
                                  <div className="flex items-baseline justify-center gap-1">
                                    <span className="text-sm text-muted-foreground">R$</span>
                                    <span className="text-4xl font-bold tracking-tight">
                                      {monthlyPrice.toFixed(2).replace('.', ',')}
                                    </span>
                                    <span className="text-sm text-muted-foreground">/mês</span>
                                  </div>
                                  <p className="mt-1.5 text-xs text-muted-foreground">
                                    ou{' '}
                                    <span className="font-semibold text-foreground">
                                      R$ {yearlyMonthlyPrice.toFixed(2).replace('.', ',')}
                                    </span>
                                    /mês no anual
                                    {yearlyDiscount > 0 && (
                                      <span className="ml-1 font-medium text-emerald-600">
                                        · economize {yearlyDiscount}%
                                      </span>
                                    )}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Features */}
                          <div className="px-5 pt-4 pb-4 flex-1 border-t border-border/40">
                            {plan.plan_features && plan.plan_features.length > 0 ? (
                              <ul className="space-y-1.5">
                                {plan.plan_features.slice(0, 7).map((pf) => (
                                  <FeatureItem
                                    key={pf.id}
                                    text={pf.name}
                                    enabled={pf.is_enabled}
                                    highlight={pf.is_highlighted && pf.is_enabled}
                                  />
                                ))}
                                {plan.plan_features.length > 7 && (
                                  <li className="pt-1 text-xs font-medium text-primary">
                                    +{plan.plan_features.length - 7} recursos adicionais
                                  </li>
                                )}
                              </ul>
                            ) : (
                              <div className="text-center py-4 text-muted-foreground">
                                <p className="text-sm">Funcionalidades em preparação</p>
                              </div>
                            )}
                          </div>

                          {/* CTA */}
                          <div className="px-5 pb-5">
                            {isCurrentPlan
                              ? renderCtaDoPlanoAtual(plan, isFree)
                              : renderCtaDePlanoNaoAtual(plan, isPendingPlan, isPopular, isFree, monthlyPrice)}
                          </div>
                        </Card>
                      );
                    })
                  )}
                </div>

                {/* Cancelar assinatura (chamado 7116): só a assinatura, não a conta.
                    Some depois de pedido — a data aparece no card Plano Atual. */}
                {currentSubscription?.is_active && canManagePlan && !cancelamentoAgendado && (
                  <div className="mt-8 pt-6 border-t border-border/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">Cancelar assinatura</p>
                      <p className="text-xs text-muted-foreground">
                        Você mantém os recursos até o fim do período contratado e depois passa para o Plano Gratuito. Seus dados continuam salvos.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className={CANCEL_BUTTON_CLASS}
                      onClick={handleOpenCancelSubscription}
                    >
                      Cancelar Assinatura
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancelar assinatura (chamado 7116) */}
      {currentSubscription && (
        <CancelSubscriptionDialog
          open={isCancelDialogOpen}
          planName={currentSubscription.plan_name ?? 'seu plano'}
          effectiveDate={cancelamento?.effective_date ?? ''}
          inTrial={emTeste}
          submitting={isCancellingSubscription}
          onOpenChange={setIsCancelDialogOpen}
          onConfirm={handleCancelSubscription}
        />
      )}

      {/* Email Change Dialog */}
      <Dialog open={isEmailChangeDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsEmailVerificationSent(false);
          setNewEmail('');
          setEmailVerificationCode('');
        }
        setIsEmailChangeDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Alterar E-mail
            </DialogTitle>
            <DialogDescription>
              {isEmailVerificationSent 
                ? 'Digite o código de 6 dígitos enviado para o novo e-mail'
                : 'Digite o novo endereço de e-mail para receber o código de verificação'}
            </DialogDescription>
          </DialogHeader>
          
          {!isEmailVerificationSent ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="newEmail">Novo E-mail</Label>
                <Input
                  id="newEmail"
                  type="email"
                  placeholder="novo@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
                <p className="flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Um código de verificação será enviado para o novo e-mail. Você precisará confirmá-lo para concluir a alteração.</span>
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Mail className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Enviamos um código para <span className="font-medium text-foreground">{newEmail}</span>
                </p>
              </div>
              
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={emailVerificationCode}
                  onChange={setEmailVerificationCode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <p className="text-center text-sm text-muted-foreground">
                Não recebeu? <button className="text-primary hover:underline" onClick={handleSendEmailVerification}>Reenviar código</button>
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailChangeDialogOpen(false)}>
              Cancelar
            </Button>
            {!isEmailVerificationSent ? (
              <Button 
                onClick={handleSendEmailVerification}
                disabled={isSendingEmailCode || !newEmail || newEmail === profileData.email}
              >
                {isSendingEmailCode && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enviar Código
              </Button>
            ) : (
              <Button 
                onClick={handleVerifyEmailCode}
                disabled={isVerifyingEmail || emailVerificationCode.length !== 6}
              >
                {isVerifyingEmail && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Verificar e Alterar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={is2FASetupDialogOpen} onOpenChange={setIs2FASetupDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Configurar Autenticação de Dois Fatores
            </DialogTitle>
            <DialogDescription>
              {twoFASetupStep === 'qrcode' 
                ? 'Escaneie o QR code com seu aplicativo autenticador'
                : 'Digite o código de 6 dígitos do seu aplicativo'}
            </DialogDescription>
          </DialogHeader>
          
          {twoFASetupStep === 'qrcode' ? (
            <div className="space-y-6 py-4">
              {/* QR Code */}
              <div className="flex flex-col items-center">
                <div className="p-4 bg-white rounded-xl border border-border shadow-sm">
                  <img 
                    src={getQRCodeUrl()}
                    alt="QR Code para configuração 2FA"
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Use o Google Authenticator, Authy ou outro app compatível
                </p>
              </div>
              
              {/* Manual code */}
              <div className="bg-muted/30 rounded-lg p-4 border border-border/40">
                <p className="text-sm font-medium mb-2">Não consegue escanear? Use o código manual:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-background px-3 py-2 rounded-lg text-sm font-mono border border-border">
                    {twoFASecret}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(twoFASecret)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <Button 
                className="w-full" 
                onClick={() => setTwoFASetupStep('verify')}
              >
                Continuar
              </Button>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <KeyRound className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Digite o código de 6 dígitos exibido no seu aplicativo autenticador
                </p>
              </div>
              
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={twoFACode}
                  onChange={setTwoFACode}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setTwoFASetupStep('qrcode')}
                >
                  Voltar
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleVerify2FASetup}
                  disabled={isVerifying2FA || twoFACode.length !== 6}
                >
                  {isVerifying2FA && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Ativar 2FA
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Disable 2FA Dialog */}
      <Dialog open={isDisable2FADialogOpen} onOpenChange={setIsDisable2FADialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Shield className="h-5 w-5" />
              Desativar Autenticação de Dois Fatores
            </DialogTitle>
            <DialogDescription>
              Para desativar o 2FA, digite o código do seu aplicativo autenticador
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-800 dark:text-red-200">
              <p className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Desativar o 2FA reduzirá a segurança da sua conta. Você poderá reativá-lo a qualquer momento.</span>
              </p>
            </div>
            
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={disable2FACode}
                onChange={setDisable2FACode}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDisable2FADialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={isVerifying2FA || disable2FACode.length !== 6}
            >
              {isVerifying2FA && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Desativar 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Plan Confirmation */}
      <SubscriberProfileCompletionModal
        open={showProfileCompletion}
        user={user}
        reason="subscribe"
        // Fechar é desistir da assinatura em andamento.
        onClose={() => {
          setShowProfileCompletion(false);
          setPendingPaidChange(null);
        }}
        onCompleted={() => {
          setShowProfileCompletion(false);
          if (pendingPaidChange) {
            setPendingPaidChange(null);
            // Perfil completo → segue com a troca para o plano pago (checkout VIO).
            void executePaidChange(pendingPaidChange.target, pendingPaidChange.cycle);
          }
        }}
      />

      {trialSummary && currentSubscription && (
        <TrialSubscribeDialog
          open={isTrialSubscribeOpen}
          planName={currentSubscription.plan_name ?? 'seu plano'}
          quotes={trialSummary.activation_quotes}
          submitting={isChangingPlan}
          onOpenChange={setIsTrialSubscribeOpen}
          onConfirm={(cycle) => void handleTrialSubscribe(cycle)}
        />
      )}

      <Dialog open={isChangePlanDialogOpen} onOpenChange={setIsChangePlanDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Alteração de Plano</DialogTitle>
            <DialogDescription>
              {currentSubscription?.plan_name 
                ? `Você está alterando do plano ${currentSubscription.plan_name} para o plano ${selectedPlan?.name}.`
                : `Você está assinando o plano ${selectedPlan?.name}.`
              }
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="py-4 space-y-4">
              {/* Seleção do ciclo de cobrança — escolhida no momento da contratação,
                  já que a tela de planos não tem mais o toggle Mensal/Anual. Na
                  migração de periodicidade do próprio plano o ciclo já vem
                  definido pelo botão do card, então o seletor não aparece. */}
              {selectedPlan.price_monthly > 0 && selectedPlan.id !== currentSubscription?.plan_id && (
                <div className="flex justify-center">
                  <div className="inline-flex items-center p-1 bg-muted rounded-full">
                    <LockedActionHint
                      message={annualLockUntil ? annualLockToMonthlyMessage(annualLockUntil) : null}
                    >
                      <button
                        type="button"
                        onClick={() => setBillingCycle('monthly')}
                        className={cn(
                          'px-5 py-2 rounded-full text-sm font-medium transition-all',
                          billingCycle === 'monthly'
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        Mensal
                      </button>
                    </LockedActionHint>
                    <button
                      type="button"
                      onClick={() => setBillingCycle('yearly')}
                      className={cn(
                        'px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2',
                        billingCycle === 'yearly'
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      Anual
                      {yearlyDiscountPercent(selectedPlan.price_monthly, selectedPlan.price_yearly) > 0 && (
                        <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">
                          -{yearlyDiscountPercent(selectedPlan.price_monthly, selectedPlan.price_yearly)}%
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
              <div className="bg-muted/30 rounded-lg p-4 border border-border/40">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-medium">{selectedPlan.name}</span>
                  <span className="text-2xl font-bold">
                    {formatBRL(
                      billingCycle === 'monthly' ? selectedPlan.price_monthly : selectedPlan.price_yearly
                    )}
                    <span className="text-sm font-normal text-muted-foreground">
                      {billingCycle === 'monthly' ? '/mês' : '/ano'}
                    </span>
                  </span>
                </div>
                {billingCycle === 'yearly' && selectedPlan.price_yearly > 0 && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Equivalente: {formatBRL(selectedPlan.price_yearly / MESES_NO_ANO)}
                  </p>
                )}
                {selectedPlan.plan_features && selectedPlan.plan_features.length > 0 && (
                  <ul className="space-y-1">
                    {selectedPlan.plan_features.filter(f => f.is_enabled).slice(0, 6).map((feature) => (
                      <li key={feature.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-emerald-500" />
                        {feature.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              <ImportantNotice>
                {avisoDaTroca(selectedPlan.price_monthly > 0, selectedPlan.name)}
              </ImportantNotice>
              
              {!gratuitoNoFimDoTeste(selectedPlan.price_monthly > 0) && currentSubscription && selectedPlan.price_monthly < (plans.find(p => p.id === currentSubscription.plan_id)?.price_monthly || 0) && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-200">
                  <p className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Ao fazer downgrade, você perderá acesso a alguns recursos no próximo ciclo
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsChangePlanDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleChangePlan} disabled={isChangingPlan}>
              {isChangingPlan && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Alteração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Invoice Detail Dialog */}
      <Dialog open={isInvoiceDetailDialogOpen} onOpenChange={setIsInvoiceDetailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Detalhes da Fatura
            </DialogTitle>
            <DialogDescription>
              Fatura {selectedInvoice?.number}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-6 py-4">
              {/* Invoice Header */}
              <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border border-border/40">
                <div>
                  <p className="text-sm text-muted-foreground">Número da Fatura</p>
                  <p className="text-lg font-semibold">{selectedInvoice.number}</p>
                </div>
                {getStatusBadge(selectedInvoice.status)}
              </div>
              
              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/20 rounded-lg border border-border/40">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <CalendarDays className="h-4 w-4" />
                    <span className="text-xs font-medium">Data de Emissão</span>
                  </div>
                  <p className="font-medium">{formatBrasilia(selectedInvoice.date, "dd/MM/yyyy")}</p>
                </div>
                <div className={cn(
                  "p-3 rounded-lg border",
                  selectedInvoice.status === 'overdue'
                    ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800"
                    : selectedInvoice.status === 'pending'
                    ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800"
                    : "bg-muted/20 border-border/40"
                )}>
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="h-4 w-4" />
                    <span className="text-xs font-medium">Data de Vencimento</span>
                  </div>
                  <p className={cn(
                    "font-medium",
                    selectedInvoice.status === 'overdue' && "text-red-600 dark:text-red-400"
                  )}>
                    {formatBrasilia(selectedInvoice.dueDate, "dd/MM/yyyy")}
                  </p>
                </div>
              </div>
              
              {/* Plan Info */}
              <div className="p-4 bg-muted/20 rounded-xl border border-border/40">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Crown className="h-4 w-4" />
                  <span className="text-sm font-medium">Descrição</span>
                </div>
                <p className="font-medium">{selectedInvoice.planName || 'Plano Profissional'}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedInvoice.planDescription || 'Assinatura mensal - Acesso completo a todos os recursos'}
                </p>
              </div>
              
              {/* Total */}
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatBRL(selectedInvoice.amount)}
                  </p>
                </div>
                {selectedInvoice.status === 'paid' && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Pago via</p>
                    <p className="font-medium">{selectedInvoice.paymentMethod}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {(selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'overdue') &&
              selectedInvoice?.paymentUrl && (
                <Button
                  className="w-full sm:w-auto gap-2"
                  onClick={() => {
                    if (selectedInvoice.paymentUrl) openInNewTab(selectedInvoice.paymentUrl);
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                  Pagar na VIO Pay
                </Button>
              )}
            <Button
              variant="outline"
              className="w-full sm:w-auto gap-2"
              onClick={() => {
                // Abre direto o PDF público da VIO Pay (`checkout.viobank.com.br/
                // fatura/{uuid}/pdf`). O endpoint do backend exige auth via
                // header, e `window.open` em nova aba não carrega o token —
                // resultado: redirect quebrava em 401. URL pública resolve.
                const pdfUrl = selectedInvoice?.downloadUrl
                  || (selectedInvoice?.id ? accountService.getMyInvoicePdfUrl(selectedInvoice.id) : null);
                if (pdfUrl) {
                  openInNewTab(pdfUrl);
                }
              }}
              disabled={!selectedInvoice?.downloadUrl && !selectedInvoice?.id}
            >
              <Download className="h-4 w-4" />
              Baixar PDF
            </Button>
            <Button variant="ghost" onClick={() => setIsInvoiceDetailDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
